import { useQuery } from '@tanstack/react-query';
import { ProgramIntakeSortColumn, ListIntakesResponse } from '@ab/ams-school-api/types/private/v1';
import { config } from '~/config';
import { hasKey, IsNullOrUndefined } from '~/utils';
import { useRequest } from '~/lib/useRequest';

export type IntakeSearchQuery = {
  'filter[q]'?: string;
  'filter[status]'?: string;
  'filter[campusIds]'?: string[];
  'filter[intakeTermIds]'?: string[];
  'page[size]': number;
  'page[number]': number;
  sort?: ProgramIntakeSortColumn;
};

export type SortableFields = ProgramIntakeSortColumn;
export type Intake = {
  id: string;
  programName: string;
  programUrl: string;
  programLevel: string;
  programLength: number;
  programLengthUnit: string;
  intakeTermName: string;
  campusName: string;
  status: string;
};

const defaultValue: Intake[] = [];

export function useIntakes(
  query: IntakeSearchQuery,
  options?: { onSuccess?: (data: Intake[], total: number) => void }
) {
  const request = useRequest();
  const { isLoading, isFetching, error, data, isPreviousData, isError } = useQuery(
    ['intakes', query],
    async () => {
      const response = await request<ListIntakesResponse>(
        `${config.apiHost}/private/v1/intakes_search?${convertToUrlSearchParams(query)}`,
        {
          method: 'GET',
          isExpectedResponse,
        }
      );
      const data = response.data;
      const intakes = data.map((intakeSearch) => {
        const attrs = intakeSearch.attributes;
        return {
          id: intakeSearch.id,
          programName: attrs.program.name,
          programUrl: attrs.program.url,
          programLevel: attrs.programLevel?.name ?? '',
          programLength: attrs.program.length,
          programLengthUnit: attrs.program.lengthUnit,
          campusName: attrs.campus.name,
          intakeTermName: attrs.intakeTerm.name,
          status: attrs.intake.status,
        };
      });

      // trigger on success
      options?.onSuccess?.(intakes, response.meta.pagination.total);

      return {
        // as we are using keepPreviousData, we need to know the result is based on which page
        pagination: {
          pageNumber: response.meta.pagination.pageNumber,
          pageSize: response.meta.pagination.pageSize,
        },
        intakes,
        totalIntakes: response.meta.pagination.total,
      };
    },
    {
      keepPreviousData: true,
    }
  );

  return {
    isPreviousIntakesData: isPreviousData,
    intakesPagination: data?.pagination,
    intakes: data?.intakes ?? defaultValue,
    intakesError: error,
    isIntakesError: isError,
    isFetchingIntakes: isFetching,
    isLoadingIntakes: isLoading,
    totalIntakes: data?.totalIntakes ?? 0,
  };
}

function isExpectedResponse(res: unknown): res is ListIntakesResponse {
  return Boolean(res && typeof res === 'object' && hasKey('data', res) && Array.isArray(res.data));
}

const convertToUrlSearchParams = (params: IntakeSearchQuery, baseKey?: string) => {
  const queryParams = {} as Record<string, string>;
  Object.entries(params).forEach(([k, v]) => {
    const key = (baseKey && `${baseKey}[${k}]`) || k;
    if (IsNullOrUndefined(v)) {
      return;
    }
    if (typeof v === 'string' && v.length > 0) {
      queryParams[key] = v;
    } else if (typeof v === 'number') {
      queryParams[key] = `${v}`;
    } else if (typeof v === 'boolean' && v) {
      queryParams[key] = 't';
    } else if (Array.isArray(v) && v.length > 0) {
      queryParams[key] = v.join(',');
    }
  });
  return new URLSearchParams(queryParams);
};
